import { Button, SlideOut } from '@digitools/honest-ui';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuthentication from 'src/hooks/useAuthentication';
import useTranslate from 'src/hooks/useTranslate';
import { PROVE_PROMPT_TEXT } from './ProveText';
import useExperience from 'src/hooks/useExperience';
import { Experience } from 'src/types/Experience';
import useTealium from 'src/hooks/useTealium';
import { TEALIUM_EVENT } from 'src/constants/tealiumConstants';
const ProvePrompt = () => {
    const [isPromptOpen, setIsPromptOpen] = useState(false);
    const { user } = useAuthentication();
    const { experience } = useExperience();
    const { t } = useTranslate();
    const navigate = useNavigate();
    const { trackEvent } = useTealium();
    const completeButtonRef = useRef(null);
    // 👩‍💻 local  hack to reset session storage
    // sessionStorage.setItem('skipIdentityVerify', 'false');
    // SlideOut's focus-trap auto-focuses its close button on open, so a stray Enter/Space
    // right after the prompt appears dismisses it instead of doing nothing. Moving focus to the
    // primary action once the panel has mounted (hence the 0ms timeout, to run after that
    // library effect) makes an accidental keypress safe.
    useEffect(() => {
        if (isPromptOpen) {
            const id = window.setTimeout(() => completeButtonRef.current?.focus(), 0);
            return () => window.clearTimeout(id);
        }
    }, [isPromptOpen]);
    // Creates slideout delay  ~* ✨ Effects ✨ *~ by not initializing as true on first render
    useEffect(() => {
        if (user?.isLfgUser && experience === Experience.EMPLOYEE) {
            const isIdentityVerified = user?.isIdentityVerified; // Mocked for now - user object
            const identityVerifyFailCount = user?.identityVerifyFailCount || 0; // Set to fill in 0 for undefined value
            const skipIdentityVerify = sessionStorage.getItem('skipIdentityVerify') === 'true';
            const completeIdentityVerify = sessionStorage.getItem('completeIdentityVerify') === 'true';
            if (!isIdentityVerified && !skipIdentityVerify && !identityVerifyFailCount && !completeIdentityVerify) {
                // The browser needs to actually paint the closed position at least once before the
                // "open" class is added, or there's nothing for the slide-in transition to animate
                // from and the panel just appears. A single rAF isn't reliable for this (it can still
                // land before that first paint); nesting a second rAF inside the first guarantees the
                // callback runs after a paint has happened.
                requestAnimationFrame(() => requestAnimationFrame(() => setIsPromptOpen(true)));
                trackEvent({
                    page_l3: TEALIUM_EVENT.EVENT_VERSION.HOMEPAGE,
                    event_action: TEALIUM_EVENT.EVENT_ACTION.SLIDE_OUT,
                    event_type: TEALIUM_EVENT.EVENT_TYPE.MIGRATION,
                    event_name: 'verify your identity',
                    event_version: TEALIUM_EVENT.EVENT_VERSION.HOMEPAGE
                });
            }
        }
    }, [user]);
    const closePrompt = () => {
        setIsPromptOpen(false);
    };
    const skipForNow = () => {
        closePrompt();
        sessionStorage.setItem('skipIdentityVerify', 'true');
        trackEvent({
            event_action: TEALIUM_EVENT.EVENT_ACTION.LINK,
            event_type: TEALIUM_EVENT.EVENT_TYPE.VERIFY_YOUR_IDENTITY,
            event_name: 'skip for now',
        });
    };
    const verifyNow = () => {
        setIsPromptOpen(false);
        sessionStorage.setItem('completeIdentityVerify', 'true');
        trackEvent({
            event_action: TEALIUM_EVENT.EVENT_ACTION.LINK,
            event_type: TEALIUM_EVENT.EVENT_TYPE.VERIFY_YOUR_IDENTITY,
            event_name: 'complete identity verification',
        });
        //TODO: could add in delay for some slide out steeze before navigation - maybe 1.5 second setTimeout (make sure tests still pass)
        navigate('/prove');
    };
    return (<>
    {user?.isLfgUser && experience === Experience.EMPLOYEE &&
            <SlideOut heading={t(PROVE_PROMPT_TEXT.HEADING)} closeLabel={t(PROVE_PROMPT_TEXT.CLOSE_LABEL)} 
            // honest-ui's headingSize controls visual size only (SlideOut hardcodes the
            // semantic tag to h3 regardless), so this doesn't change heading semantics. 'h2' is
            // the token that actually matches the Figma spec (30px/36px, serif) — 'h4' renders at
            // 20px/26px, which is what looked undersized against the mock.
            headingSize='h2' inline={false} isOpen={isPromptOpen} 
            // The close button, Escape, and outside click just close the panel for this render —
            // unlike "Skip for now", they don't set the sessionStorage flag, so an accidental
            // dismissal (e.g. a stray mouseup while resizing the window lands outside the panel and
            // SlideOut treats it as an outside click) doesn't permanently suppress the prompt for
            // the rest of the session. They're also a distinct interaction from "Skip for now" —
            // track them under their own event_name rather than reusing that CTA's.
            onOpenChange={(isOpen) => {
                    if (isOpen)
                        return;
                    closePrompt();
                    trackEvent({
                        event_action: TEALIUM_EVENT.EVENT_ACTION.LINK,
                        event_type: TEALIUM_EVENT.EVENT_TYPE.VERIFY_YOUR_IDENTITY,
                        event_name: 'close',
                    });
                }}>
        {
                // Content layout for this prompt's own children, not an override of SlideOut: the gap
                // below the body text and the column/gap in the actions group are specific to this
                // card, so they're set here with honest-ui's own spacing tokens rather than literal
                // pixel values.
                }
        <div style={{ marginBottom: 'var(--spacing-xl-2)' }}>
          <p>{t(PROVE_PROMPT_TEXT.P1)}</p>
          <p>{t(PROVE_PROMPT_TEXT.P2)}</p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--spacing-xl)' }}>
          <Button ref={completeButtonRef} type='primary' label={t(PROVE_PROMPT_TEXT.COMPLETE_BUTTON)} onClick={() => verifyNow()}/>
          <Button type='text' label={t(PROVE_PROMPT_TEXT.SKIP_BUTTON)} onClick={() => skipForNow()}/>
        </div>
      </SlideOut>}
    </>);
};
export default ProvePrompt;
